// Apps (02-DESIGN/01-identity/04-apps.md § the registration, § what the
// employee receives, § revocation and rotation; 01-the-console.md § the
// words, § the moves of 2026-11-01): a person connects an app that acts as
// them; an admin one that acts as the workspace. One list, two scopes where
// the role has both: an app's name and *acts as you* or *acts as Acme* under
// it, and its state. A row opens the app's page, `apps/<id>` (AppPage.tsx);
// *New key*, *Restore* and *Disconnect* are in its row menu. *Connect an
// app*, *Use your AI client* and the key a *New key* hands over are forms in
// the page's place (forms.tsx); the key and the API endpoint appear only at
// their last step.

import { Plus, Sparkles } from "lucide-react";
import { useState } from "react";

import { tenantPath } from "../../address/address.ts";
import { usePage } from "../../platform/session.tsx";
import {
  Button,
  Empty,
  NotAllowed,
  PageHead,
  Pending,
  Pill,
  ScopeFilter,
  useLoad,
  type Scope,
} from "../../ui/components.tsx";
import { useFormState } from "../../ui/form.tsx";
import { Subjects, type App, type AppsListReply } from "../../wire/auth.ts";
import { SERVICE } from "../../wire/identity.ts";
import { Table, type Column } from "../../ui/table.tsx";
import { useSurface } from "../surface.tsx";
import { useAppActs } from "./acts.tsx";
import { WHICH_WAY } from "./desktop.ts";
import { ConnectApp, DesktopClient, KeyForm } from "./forms.tsx";
import { appState } from "./state.ts";

/** The forms the Apps page shows in its place. */
type AppsForm = "connect" | "desktop" | "key";

export function AppsPage() {
  const s = useSurface();
  const { platform } = usePage();
  const [apps, reload] = useLoad("apps", () =>
    s.bus.call<AppsListReply>(SERVICE, Subjects.appsList(), undefined),
  );
  const form = useFormState<AppsForm>();
  const acts = useAppActs(s, reload, form.open, () => {
    form.show("key");
  });
  const both = s.offered("apps:list:any");
  const [scope, setScope] = useState<Scope>("yours");
  const gateway = platform.gateway_url || undefined;

  const all = apps.state === "ready" ? apps.value.apps : [];
  const mine = all.filter((a) => a.registrant.subject === s.me);
  const shown = both && scope === "everyone" ? all : both ? mine : all;

  if (form.open === "connect") {
    return (
      <ConnectApp
        onClose={form.close}
        onConnected={(reply) => {
          reload();
          // The key step takes the form's place: Back from it returns to the
          // list, and Forward finds no empty form and no key.
          form.close();
          acts.connected(reply);
        }}
      />
    );
  }
  if (form.open === "desktop") {
    return <DesktopClient gateway={gateway} onClose={form.close} />;
  }
  // The key is held in memory only while its step is open: a step reopened
  // without it — a refresh, Forward — has nothing to show, and the list
  // stands in its place.
  if (acts.key !== undefined) {
    return <KeyForm reply={acts.key.reply} fresh={acts.key.fresh} gateway={gateway} onClose={form.close} />;
  }

  const connect = s.offered("apps:register:personal") ? (
    <Button
      kind="primary"
      onClick={() => {
        form.show("connect");
      }}
    >
      <Plus strokeWidth={1.8} aria-hidden="true" />
      Connect an app
    </Button>
  ) : undefined;
  const desktop = (
    <Button
      onClick={() => {
        form.show("desktop");
      }}
    >
      <Sparkles strokeWidth={1.8} aria-hidden="true" />
      Use your AI client
    </Button>
  );
  const columns: Column<App>[] = [
    {
      key: "app",
      header: "App",
      width: "minmax(0, 2fr)",
      value: (a) => a.name,
      text: (a) => `${a.name} acts as ${acts.actsAs(a)}`,
      cell: (a) => (
        <span className="cell">
          <span className="cell-main">{a.name}</span>
          <span className="cell-sub">acts as {acts.actsAs(a)}</span>
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "120px",
      facet: "Status",
      value: (a) => appState(a).word,
      cell: (a) => {
        const st = appState(a);
        return <Pill tone={st.tone}>{st.word}</Pill>;
      },
    },
  ];

  return (
    <>
      <PageHead
        title="Apps"
        line={
          both
            ? `The tools that reach Impire as you, and as ${s.name}.`
            : "The tools that reach Impire as you."
        }
        action={
          <>
            {desktop}
            {connect}
          </>
        }
      />
      <p className="small muted" style={{ marginBottom: 20 }}>
        {WHICH_WAY}
      </p>
      {both && (
        <ScopeFilter
          value={scope}
          onChange={setScope}
          counts={{ yours: mine.length, everyone: all.length }}
        />
      )}
      {acts.err !== undefined && <NotAllowed err={acts.err} />}
      {apps.state === "pending" && <Pending what="Reading the apps" />}
      {apps.state === "refused" && <NotAllowed err={apps.err} what="Apps" />}
      {apps.state === "ready" && shown.length === 0 && (
        <Empty action={connect}>
          {both && scope === "everyone"
            ? `Nobody in ${s.name} has connected an app yet.`
            : "You have not connected an app yet. Connect one and paste what you receive into it."}
        </Empty>
      )}
      {shown.length > 0 && (
        <Table
          label="Apps"
          rows={shown}
          rowKey={(a) => a.id}
          sort={{ key: "app", dir: "asc" }}
          mark={acts.marked}
          columns={columns}
          to={(a) => tenantPath(s.tenant, "apps", a.id)}
          menu={acts.menuFor}
        />
      )}
    </>
  );
}
